import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { ChevronDownIcon, Columns3Icon, TargetIcon, XIcon } from "lucide-react";

import { useUserSheet } from "./userSheetContext";

import { userSheetViewStore } from "@/stores/userSheetView";
import type { UserSheetTab } from "./types";

import { Center, Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { RECOMMENDED_JOBS_LIMIT } from "@/lib/constants";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { storage } from "@/lib/storage";
import { novaClient } from "@/lib/trpc";
import { cn, isUserTyping } from "@/lib/utils";
import type { UserSheetFocusMode } from "@/types";

const TAB_TRIGGER_CLASSNAME =
  "rounded-none border-0 border-b-2 border-transparent bg-transparent px-3 py-2.5 font-normal text-slate-500 shadow-none hover:text-slate-700 data-[state=active]:border-0 data-[state=active]:border-b-2 data-[state=active]:border-b-primary-900 data-[state=active]:bg-transparent data-[state=active]:font-medium data-[state=active]:text-primary-900 data-[state=active]:shadow-none";
// Additional panes beyond the primary tab pane.
const MAX_SPLIT_PANES = 2;
const MIN_PANE_WIDTH_PERCENT = 20;

// The sheet's tab area: top-level tabs, the "more" dropdown for overflow tabs,
// and split panes so two tabs can be viewed side by side.
export const UserSheetTabs = ({
  activeTab: controlledActiveTab,
  defaultTab,
  onActiveTabChange,
  scrollHeader,
  stickyOffsetClassName = "top-0",
  tabBarActions,
  tabs,
}: {
  // Optional controlled tab, for hosts that jump tabs themselves. Omitted, the
  // tab area owns its own state.
  activeTab?: string;
  defaultTab?: string;
  onActiveTabChange?: (value: string) => void;
  // Rendered above the tab bar inside the primary pane's scroll container so
  // it scrolls away under the sticky identity row. In split view it moves above
  // both panes instead, where it stays put.
  scrollHeader?: ReactNode;
  stickyOffsetClassName?: string;
  tabBarActions?: ReactNode;
  tabs: UserSheetTab[];
}) => {
  /* Context */
  const {
    closeCommunications,
    communicationsScope,
    communicationsTab,
    communicationsWidthPercent,
    isCommunicationsOpen,
    jobId,
    openCommunications,
    setCommunicationsScope,
    setCommunicationsWidthPercent,
    workerId,
  } = useUserSheet();
  const {
    isActivityRailCollapsed,
    isResumesCollapsed,
    resumeZoom,
    setIsActivityRailCollapsed,
    setIsResumesCollapsed,
    setResumeZoom,
    setSplitTabValues,
    splitTabValues,
  } = userSheetViewStore();
  const headerPaddingClassName = isCommunicationsOpen ? "pl-10 pr-6" : "px-6";
  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useSidebar();

  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();
  // Fetched here so the tab bar can show the count; the Recommended Jobs tab
  // reads the same query from cache with identical args.
  const hasRecommendedTab = tabs.some((tab) => tab.value === "recommended");
  const recommendedJobsQuery =
    novaClient.internal.jobs.recommendations.findRecommendedInternalJobsByUser.useQuery(
      {
        excludeJobIds: jobId ? [jobId] : [],
        limit: RECOMMENDED_JOBS_LIMIT,
        userId: workerId,
      },
      { enabled: hasRecommendedTab },
    );

  /* React Hooks */
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    defaultTab || tabs[0]?.value || "",
  );
  const [focusMode, setFocusMode] = useState<UserSheetFocusMode | undefined>(
    () =>
      currentUser ? storage.getUserSheetFocusMode(currentUser.id) : undefined,
  );
  // Captured at mount, before the persistence effect below can overwrite the
  // stored flag with this render's (still inactive) state.
  const [shouldAutoActivateFocusMode] = useState(() =>
    currentUser ? storage.getUserSheetFocusModeActive(currentUser.id) : false,
  );
  const [isFocusModeMenuOpen, setIsFocusModeMenuOpen] = useState(false);
  const [isFocusModeActive, setIsFocusModeActive] = useState(false);
  const [viewBeforeFocusMode, setViewBeforeFocusMode] =
    useState<UserSheetFocusMode>();
  const [paneWidthsPercent, setPaneWidthsPercent] = useState([100]);
  const [isResizingPanes, setIsResizingPanes] = useState(false);
  const didCollapseSidebarRef = useRef(false);
  const panesRowRef = useRef<HTMLDivElement>(null);
  // One scroll container per pane, keyed by pane index (null = primary).
  const paneScrollElementsRef = useRef(
    new Map<number | null, HTMLDivElement>(),
  );

  // Review flows swap the person without remounting the sheet, which would
  // otherwise keep the previous candidate's scroll position.
  useEffect(() => {
    paneScrollElementsRef.current.forEach((element) => {
      element.scrollTo({ top: 0 });
    });
  }, [workerId]);

  useEffect(() => {
    return () => {
      if (didCollapseSidebarRef.current) {
        setSidebarOpen(true);
      }
    };
    // The boolean setter is safe to capture at mount; this cleanup must only
    // run when the sheet unmounts, not whenever sidebar state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Helpers */
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const activeTab = controlledActiveTab || uncontrolledActiveTab;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const setActiveTab = onActiveTabChange || setUncontrolledActiveTab;

  useEffect(() => {
    if (!isFocusModeActive || !focusMode) return;

    const hasMatchingSplitTabs =
      splitTabValues.length === focusMode.splitTabValues.length &&
      splitTabValues.every(
        (tabValue, index) => tabValue === focusMode.splitTabValues[index],
      );
    const isMatchingFocusMode =
      activeTab === focusMode.activeTab &&
      hasMatchingSplitTabs &&
      isCommunicationsOpen === focusMode.isCommunicationsOpen &&
      communicationsWidthPercent === focusMode.communicationsWidthPercent &&
      paneWidthsPercent.every(
        (width, index) => width === focusMode.paneWidthsPercent[index],
      );

    if (!isMatchingFocusMode) {
      setIsFocusModeActive(false);
      setViewBeforeFocusMode(undefined);
    }
  }, [
    activeTab,
    communicationsWidthPercent,
    focusMode,
    isCommunicationsOpen,
    isFocusModeActive,
    paneWidthsPercent,
    splitTabValues,
  ]);

  // Persisted so the next sheet reopens in focus mode when it was left on.
  useEffect(() => {
    if (!currentUser) return;
    storage.setUserSheetFocusModeActive(currentUser.id, isFocusModeActive);
  }, [currentUser, isFocusModeActive]);

  useEffect(() => {
    const paneCount = splitTabValues.length + 1;
    if (paneWidthsPercent.length === paneCount) return;
    setPaneWidthsPercent(
      Array.from({ length: paneCount }, () => 100 / paneCount),
    );
  }, [paneWidthsPercent.length, splitTabValues.length]);

  useEffect(() => {
    if (!isResizingPanes) return;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isResizingPanes]);
  const recommendedJobsCount = recommendedJobsQuery.data?.length;
  const isSplitView = splitTabValues.length > 0;
  const resolvedTabs = tabs.map((tab) =>
    tab.value === "recommended" && recommendedJobsCount != null
      ? { ...tab, count: recommendedJobsCount }
      : tab,
  );
  const visibleTabs = resolvedTabs.filter((tab) => !tab.isMoreTab);
  const moreTabs = resolvedTabs.filter((tab) => tab.isMoreTab);
  const canAddSplitPane = splitTabValues.length < MAX_SPLIT_PANES;

  /* Handlers */
  const handleTabChange = (setTab: (value: string) => void) => {
    return (value: string) => {
      mixpanelUtils.trackTabView(
        getScreenFromPathname(window.location.pathname),
        value,
      );
      setIsFocusModeActive(false);
      setTab(value);
    };
  };

  const handleSplitTabs = () => {
    mixpanelUtils.trackButtonClick(
      "Split tabs",
      getScreenFromPathname(window.location.pathname),
    );
    if (isSidebarOpen) {
      didCollapseSidebarRef.current = true;
      setSidebarOpen(false);
    }
    setIsFocusModeActive(false);
    const nextSplitTabValues = [...splitTabValues, activeTab];
    const paneCount = nextSplitTabValues.length + 1;
    setSplitTabValues(nextSplitTabValues);
    setPaneWidthsPercent(
      Array.from({ length: paneCount }, () => 100 / paneCount),
    );
  };

  const handleCloseSplitPane = (index: number) => {
    mixpanelUtils.trackButtonClick(
      "Close split tab",
      getScreenFromPathname(window.location.pathname),
    );
    setIsFocusModeActive(false);
    const removedPaneIndex = index + 1;
    const remainingWidths = paneWidthsPercent.filter(
      (_, paneIndex) => paneIndex !== removedPaneIndex,
    );
    const remainingTotal = remainingWidths.reduce(
      (total, width) => total + width,
      0,
    );
    setPaneWidthsPercent(
      remainingWidths.map((width) => (width / remainingTotal) * 100),
    );
    setSplitTabValues(
      splitTabValues.filter((_, tabIndex) => tabIndex !== index),
    );
  };

  const handleSetSplitPaneTab = (index: number, value: string) => {
    setIsFocusModeActive(false);
    setSplitTabValues(
      splitTabValues.map((existingTab, tabIndex) =>
        tabIndex === index ? value : existingTab,
      ),
    );
  };

  const handleSetFocusMode = () => {
    const nextFocusMode = {
      activeTab,
      communicationsScope: communicationsScope ?? null,
      communicationsWidthPercent,
      isActivityRailCollapsed,
      isCommunicationsOpen,
      isResumesCollapsed,
      paneWidthsPercent: [...paneWidthsPercent],
      resumeZoom: resumeZoom ?? null,
      splitTabValues: [...splitTabValues],
    };
    setFocusMode(nextFocusMode);
    setIsFocusModeActive(false);
    setViewBeforeFocusMode(undefined);
    setIsFocusModeMenuOpen(false);
    if (currentUser) {
      storage.setUserSheetFocusMode(currentUser.id, nextFocusMode);
    }
  };

  // Shared by the toggle button, the keyboard shortcut, and the reopen effect.
  const activateFocusMode = () => {
    if (!focusMode) return;
    setViewBeforeFocusMode({
      activeTab,
      communicationsScope: communicationsScope ?? null,
      communicationsWidthPercent,
      isActivityRailCollapsed,
      isCommunicationsOpen,
      isResumesCollapsed,
      paneWidthsPercent: [...paneWidthsPercent],
      resumeZoom: resumeZoom ?? null,
      splitTabValues: [...splitTabValues],
    });
    // A saved split needs the horizontal room, same as the split button.
    if (focusMode.splitTabValues.length > 0 && isSidebarOpen) {
      didCollapseSidebarRef.current = true;
      setSidebarOpen(false);
    }
    setActiveTab(focusMode.activeTab);
    setSplitTabValues([...focusMode.splitTabValues]);
    setCommunicationsWidthPercent(focusMode.communicationsWidthPercent);
    setPaneWidthsPercent([...focusMode.paneWidthsPercent]);
    setIsActivityRailCollapsed(focusMode.isActivityRailCollapsed);
    setIsResumesCollapsed(focusMode.isResumesCollapsed);
    setResumeZoom(focusMode.resumeZoom ?? undefined);
    setCommunicationsScope(focusMode.communicationsScope ?? undefined);
    setIsFocusModeActive(true);
    if (focusMode.isCommunicationsOpen === isCommunicationsOpen) return;
    if (focusMode.isCommunicationsOpen) {
      openCommunications(communicationsTab);
      return;
    }
    closeCommunications();
  };

  const handleToggleFocusMode = () => {
    if (isFocusModeActive) {
      setIsFocusModeActive(false);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      setActiveTab(viewBeforeFocusMode?.activeTab || "candidacy");
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      setSplitTabValues([...(viewBeforeFocusMode?.splitTabValues || [])]);
      if (viewBeforeFocusMode) {
        setCommunicationsWidthPercent(
          viewBeforeFocusMode.communicationsWidthPercent,
        );
        setPaneWidthsPercent([...viewBeforeFocusMode.paneWidthsPercent]);
        setIsActivityRailCollapsed(viewBeforeFocusMode.isActivityRailCollapsed);
        setIsResumesCollapsed(viewBeforeFocusMode.isResumesCollapsed);
        setResumeZoom(viewBeforeFocusMode.resumeZoom ?? undefined);
        setCommunicationsScope(
          viewBeforeFocusMode.communicationsScope ?? undefined,
        );
      }
      if (viewBeforeFocusMode?.isCommunicationsOpen !== isCommunicationsOpen) {
        if (viewBeforeFocusMode?.isCommunicationsOpen) {
          openCommunications(communicationsTab);
        } else {
          closeCommunications();
        }
      }
      setViewBeforeFocusMode(undefined);
      return;
    }

    activateFocusMode();
  };

  // Reopen in focus mode when the staffer last left the sheet with it on.
  useEffect(() => {
    if (!shouldAutoActivateFocusMode) return;
    activateFocusMode();
    // Applies the saved layout once, at mount; later changes are the toggle's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isUserTyping() || event.ctrlKey || event.altKey) return;
      if (!event.metaKey || !event.shiftKey) return;
      if (event.key.toLowerCase() !== "f") return;

      event.preventDefault();
      mixpanelUtils.trackKeyboardShortcutUsed(
        "Cmd+Shift+F (Focus Mode)",
        getScreenFromPathname(window.location.pathname),
      );
      handleToggleFocusMode();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  });

  const handlePaneResize = (
    dividerIndex: number,
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (!isResizingPanes) return;
    const panesRow = panesRowRef.current;
    if (!panesRow) return;

    const bounds = panesRow.getBoundingClientRect();
    const pointerPercent = ((event.clientX - bounds.left) / bounds.width) * 100;
    const pairStartPercent = paneWidthsPercent
      .slice(0, dividerIndex)
      .reduce((total, width) => total + width, 0);
    const pairWidthPercent =
      paneWidthsPercent[dividerIndex] + paneWidthsPercent[dividerIndex + 1];
    const nextLeftWidth = Math.min(
      pairWidthPercent - MIN_PANE_WIDTH_PERCENT,
      Math.max(MIN_PANE_WIDTH_PERCENT, pointerPercent - pairStartPercent),
    );

    setPaneWidthsPercent(
      paneWidthsPercent.map((width, index) => {
        if (index === dividerIndex) return nextLeftWidth;
        if (index === dividerIndex + 1) return pairWidthPercent - nextLeftWidth;
        return width;
      }),
    );
  };

  const handleFocusModeMenuOpenChange = (isOpen: boolean) => {
    if (!isOpen) setIsFocusModeMenuOpen(false);
  };

  /* Render */
  const renderTabLabel = (tab: UserSheetTab) => {
    if (!tab.count && !tab.indicator) return tab.label;

    return (
      <Row className="items-center gap-1.5">
        {tab.label}
        {tab.indicator}
        {tab.count ? (
          <Center className="h-4 min-w-4 shrink-0 rounded-full bg-slate-200 px-1">
            <Text className="text-[10px] font-medium leading-none text-slate-600">
              {tab.count > 9 ? "9+" : tab.count}
            </Text>
          </Center>
        ) : null}
      </Row>
    );
  };

  const renderMoreTabs = (
    currentTab: string,
    setTab: (value: string) => void,
  ) => {
    if (moreTabs.length === 0) return null;

    const pinnedTab = moreTabs.find((tab) => tab.value === currentTab);

    return (
      <>
        {/* The active "more" tab pins next to the chevron so it stays visible. */}
        {pinnedTab ? (
          <TabsTrigger
            className={TAB_TRIGGER_CLASSNAME}
            value={pinnedTab.value}
          >
            {renderTabLabel(pinnedTab)}
          </TabsTrigger>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-auto shrink-0 rounded-none px-2 py-2.5 text-slate-400 shadow-none hover:bg-transparent hover:text-slate-600"
              size="icon"
              type="button"
              variant="ghost"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-[10rem] rounded-md border border-slate-200 bg-white p-1 shadow-md"
            sideOffset={4}
          >
            {moreTabs.map((tab) => (
              <DropdownMenuItem
                className={cn(
                  "cursor-pointer rounded-sm px-2.5 py-2 text-sm text-slate-700 focus:bg-slate-100 focus:text-slate-900",
                  tab.value === currentTab &&
                    "bg-slate-50 font-medium text-slate-900",
                )}
                key={tab.value}
                onSelect={() => {
                  handleTabChange(setTab)(tab.value);
                }}
              >
                {renderTabLabel(tab)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  };

  const renderSplitTabsButton = () => {
    if (!canAddSplitPane) return null;

    return (
      <Button
        className="h-8 w-8 shrink-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        onClick={handleSplitTabs}
        size="icon"
        title="Split tabs"
        type="button"
        variant="ghost"
      >
        <Columns3Icon className="h-4 w-4" />
      </Button>
    );
  };

  const renderFocusModeControl = () => {
    const getTooltipText = () => {
      if (isFocusModeActive) return "Turn off focus mode (⌘⇧F)";
      if (focusMode) return "Turn on focus mode (⌘⇧F) · Right click to update";
      return "Right click to save your focus mode";
    };

    return (
      <DropdownMenu
        onOpenChange={handleFocusModeMenuOpenChange}
        open={isFocusModeMenuOpen}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                className={cn(
                  "h-8 w-8 shrink-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700",
                  !focusMode &&
                    "cursor-default text-slate-300 hover:bg-transparent hover:text-slate-300",
                  isFocusModeActive &&
                    "bg-primary-100 text-primary-800 hover:bg-primary-200 hover:text-primary-900",
                )}
                onClick={handleToggleFocusMode}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setIsFocusModeMenuOpen(true);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <TargetIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <Text className="text-sm font-normal text-slate-950">
              {getTooltipText()}
            </Text>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={handleSetFocusMode}>
            <Text className="text-sm font-normal text-slate-700">
              Save current view as focus mode
            </Text>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderCloseSplitPaneButton = (index: number) => {
    return (
      <Button
        className="h-8 w-8 shrink-0 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        onClick={() => {
          handleCloseSplitPane(index);
        }}
        size="icon"
        title="Close split tab"
        type="button"
        variant="ghost"
      >
        <XIcon className="h-4 w-4" />
      </Button>
    );
  };

  const renderTabBar = ({
    currentTab,
    paddingClassName,
    paneIndex,
    setTab,
  }: {
    currentTab: string;
    paddingClassName: string;
    paneIndex: number | null;
    setTab: (value: string) => void;
  }) => {
    const isPrimaryPane = paneIndex === null;
    const isFocusModePane = isSplitView
      ? paneIndex === splitTabValues.length - 1
      : isPrimaryPane;

    return (
      // Padding sits on the wrapper so the divider is inset, not full-width.
      // Sticky so it pins below the identity row while content scrolls under.
      <Column
        className={cn(
          "sticky z-10 shrink-0 bg-white",
          stickyOffsetClassName,
          paddingClassName,
        )}
      >
        <Row className="h-11 items-center gap-1 border-b border-slate-200">
          <Row className="h-full min-w-0 flex-1 items-center overflow-x-auto">
            <TabsList className="inline-flex h-full w-max min-w-full items-center justify-start gap-0 rounded-none border-0 bg-transparent p-0 text-slate-500 shadow-none">
              {visibleTabs.map((tab) => (
                <TabsTrigger
                  className={TAB_TRIGGER_CLASSNAME}
                  key={tab.value}
                  value={tab.value}
                >
                  {renderTabLabel(tab)}
                </TabsTrigger>
              ))}
              {renderMoreTabs(currentTab, setTab)}
            </TabsList>
          </Row>
          <Row className="ml-auto shrink-0 items-center gap-1">
            {isPrimaryPane ? tabBarActions : null}
            {isFocusModePane ? renderSplitTabsButton() : null}
            {isFocusModePane ? renderFocusModeControl() : null}
            {paneIndex !== null ? renderCloseSplitPaneButton(paneIndex) : null}
          </Row>
        </Row>
      </Column>
    );
  };

  const renderTabPane = ({
    currentTab,
    paddingClassName,
    paneIndex,
    setTab,
  }: {
    currentTab: string;
    paddingClassName: string;
    paneIndex: number | null;
    setTab: (value: string) => void;
  }) => {
    return (
      <Tabs
        className="flex min-h-0 flex-1 flex-col"
        onValueChange={handleTabChange(setTab)}
        value={currentTab}
      >
        <Column
          className="min-w-0 flex-1 overflow-y-auto"
          ref={(element: HTMLDivElement | null) => {
            if (element) {
              paneScrollElementsRef.current.set(paneIndex, element);
            } else {
              paneScrollElementsRef.current.delete(paneIndex);
            }
          }}
        >
          {paneIndex === null && !isSplitView ? (
            <Column className="pt-3">{scrollHeader}</Column>
          ) : null}
          {renderTabBar({ currentTab, paddingClassName, paneIndex, setTab })}
          {tabs.map((tab) => (
            <TabsContent className="mt-0" key={tab.value} value={tab.value}>
              {tab.content}
            </TabsContent>
          ))}
        </Column>
      </Tabs>
    );
  };

  const render = () => {
    return (
      <Column className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {/* Split panes share one header, so it spans them instead of scrolling
            inside the primary pane. */}
        {isSplitView ? (
          <Column className="shrink-0 pt-3">{scrollHeader}</Column>
        ) : null}
        <Row
          className="min-h-0 min-w-0 flex-1 overflow-hidden"
          ref={panesRowRef}
        >
          <Column
            className="min-h-0 min-w-0 shrink-0 overflow-hidden"
            style={{ width: `${String(paneWidthsPercent[0])}%` }}
          >
            {renderTabPane({
              currentTab: activeTab,
              paddingClassName: headerPaddingClassName,
              paneIndex: null,
              setTab: setActiveTab,
            })}
          </Column>
          {splitTabValues.map((splitTab, index) => (
            <Column
              className="relative min-h-0 min-w-0 shrink-0 border-l border-slate-200"
              key={`split-pane-${String(index)}`}
              style={{
                width: `${String(paneWidthsPercent[index + 1])}%`,
              }}
            >
              <Row
                className="absolute -left-1 top-0 z-20 h-full w-2 cursor-col-resize"
                onPointerCancel={() => {
                  setIsResizingPanes(false);
                }}
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  setIsResizingPanes(true);
                }}
                onPointerMove={(event) => {
                  handlePaneResize(index, event);
                }}
                onPointerUp={(event) => {
                  event.currentTarget.releasePointerCapture(event.pointerId);
                  setIsResizingPanes(false);
                }}
              />
              <Column className="min-h-0 min-w-0 flex-1 overflow-hidden">
                {renderTabPane({
                  currentTab: splitTab,
                  paddingClassName: "pl-2 pr-2 lg:pl-3 lg:pr-3",
                  paneIndex: index,
                  setTab: (value) => {
                    handleSetSplitPaneTab(index, value);
                  },
                })}
              </Column>
            </Column>
          ))}
        </Row>
      </Column>
    );
  };

  return render();
};
